import { msalInstance, authSettings, teamsAuth } from "@/auth/authInstances";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { isEmpty } from "lodash-es";
import { Agent, ChatMessage, ChatModel, Conversation, ConversationRequest, CustomGPT, DEFAULT_PERSONALIZATION, ErrorResponsePayload, GroupConversation, GroupParticipant, LibraryImage, PersistedRealtimeTurnResponse, Personalization, PresentationAsset, ProfileAnalytics, RealtimeClientSecretResponse, RealtimePrepareTurnResponse, SearchConversation, StreamErrorDetails, UserInfo } from "./models";
import { apiRequestErrorFromResponse, getErrorDetails } from "@/utils/errorMessages";
import { chatModels } from "@/constants/models";
import { getFreshTeamsToken } from "@/auth/teamsAuth";
import { getRedirectStartPage, redirectOnce } from "@/auth/authRedirect";
import { getPresentationFilename, PersonalLibraryItem, PersonalLibraryKind } from "@/components/Library/libraryUtils";
import { normalizeCompactionTimeline } from "@/utils/chatStream";

/**
 * Fetch wrapper that includes authentication token in headers
**/
/**
 * Resolves the Authorization header for the active auth mode.
 *
 * Split out of fetchWithAuth so the XHR-based file upload can reuse exactly the
 * same token handling — fetch() cannot report request-body progress, so that one
 * call has to bypass it. Returns an empty object when MSAL holds no account, which
 * keeps unauthenticated calls passing through unchanged, and rejects on the paths
 * that redirect to login.
 *
 * @returns {Promise<Record<string, string>>} Headers to merge into the request
**/
export const resolveAuthHeaders = async (): Promise<Record<string, string>> => {
  // Check if running in Teams environment
  if (teamsAuth.isInTeams) {
    // Get fresh Teams SSO token (automatically refreshes if expired)
    const token = await getFreshTeamsToken();

    if (!token) {
      console.error('Failed to get Teams auth token');
      return Promise.reject(new Error('Teams authentication failed'));
    }

    return { 'Authorization': `Bearer ${token}` };
  }

  // Otherwise use MSAL authentication
  const instance = msalInstance;
  const accounts = instance.getAllAccounts();

  if (accounts.length === 0) return {};

  const request = {
    scopes: [`api://${authSettings.domain}/${authSettings.azure_client_id}/user_impersonation`],
    account: accounts[0],
    redirectStartPage: getRedirectStartPage()
  };

  try {
    const response = await instance.acquireTokenSilent(request);
    return { 'Authorization': `Bearer ${response.accessToken}` };
  } catch (error) {
    console.error("Token acquisition failed", error);

    // Use redirect for WSL compatibility (popup/iframe has timeout issues in WSL)
    if (error instanceof InteractionRequiredAuthError) {
      console.log("Session expired - redirecting to login...");
      await redirectOnce(() => instance.acquireTokenRedirect(request));
      // Execution stops here as we're redirecting
      return Promise.reject(new Error("Redirecting for authentication"));
    }

    await redirectOnce(async () => {
      // Cache reset must be part of the shared redirect operation so concurrent
      // silent-token failures cannot clear the state of an active redirect.
      await instance.clearCache();
      window.location.replace(window.location.origin);
    });
    return Promise.reject(new Error("Redirecting for authentication"));
  }
}

export const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
  const authHeaders = await resolveAuthHeaders();
  return fetch(url, { ...options, headers: { ...options.headers, ...authHeaders } });
}



/****************************************************************************************************************************************************************************************************************************
 * GENERAL
/***************************************************************************************************************************************************************************************************************************/

/**
 * Fetches the frontend settings from the server.
 *
 * @returns {Promise<Response | null>} A promise that resolves to the response object containing the frontend settings
**/
export const getFrontendSettings = async (): Promise<Response | null> => {
  const response = await fetchWithAuth("/api/frontend_settings", {
    method: "GET",
  }).then((res) => {
    return res.json();
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return null;
  });

  return response;
}

export const getUserPreferences = async (): Promise<Personalization | null> => {
  const response = await fetchWithAuth("/api/user/preferences", { method: "GET" });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as Partial<ErrorResponsePayload> | null;
    throw apiRequestErrorFromResponse(response, payload, "Failed to load personalization settings.");
  }
  const payload = await response.json();
  const tone = payload?.tone;
  const validTones = ['default', 'professional', 'friendly', 'candid', 'quirky', 'efficient'] as const;
  return {
    nickname: typeof payload?.nickname === 'string' ? payload.nickname : DEFAULT_PERSONALIZATION.nickname,
    occupation: typeof payload?.occupation === 'string' ? payload.occupation : DEFAULT_PERSONALIZATION.occupation,
    tone: validTones.includes(tone) ? tone : DEFAULT_PERSONALIZATION.tone,
    custom_instructions: typeof payload?.custom_instructions === 'string' ? payload.custom_instructions : DEFAULT_PERSONALIZATION.custom_instructions,
  } as Personalization;
};

export const saveUserPreferences = async (preferences: Personalization): Promise<Personalization> => {
  const response = await fetchWithAuth("/api/user/preferences", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(preferences),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => null) as Partial<ErrorResponsePayload> | null;
    throw apiRequestErrorFromResponse(response, error, "Failed to save personalization settings.");
  }
  const payload = await response.json();
  return {
    nickname: typeof payload?.nickname === 'string' ? payload.nickname : preferences.nickname,
    occupation: typeof payload?.occupation === 'string' ? payload.occupation : preferences.occupation,
    tone: payload?.tone || preferences.tone,
    custom_instructions: typeof payload?.custom_instructions === 'string' ? payload.custom_instructions : preferences.custom_instructions,
  };
};

/**
 * Window event fired when a chat response completes, so the usage bar can refresh.
**/
export const USAGE_REFRESH_EVENT = "usage:refresh";

/**
 * Notifies listeners (the sidebar usage bar) that token usage may have changed.
**/
export const notifyUsageChanged = (): void => {
  window.dispatchEvent(new Event(USAGE_REFRESH_EVENT));
};

/**
 * Fetches the current user's weekly token usage.
 *
 * @returns {Promise<WeeklyUsage | null>} Usage totals for the current week, or null on error.
**/
export const getUsage = async (): Promise<import("./models").WeeklyUsage | null> => {
  const response = await fetchWithAuth("/api/usage", {
    method: "GET",
  }).then((res) => {
    return res.json();
  }).catch(() => {
    console.error("There was an issue fetching your usage.");
    return null;
  });

  return response;
}

/**
 * Fetches the weekly usage summary shown in the user's profile.
 *
 * The response is normalized here so the Profile tab is insulated from the
 * API's wire naming and can render partial historical data safely.
 */
export const getProfileAnalytics = async (): Promise<ProfileAnalytics> => {
  const response = await fetchWithAuth("/api/profile/analytics", { method: "GET" });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as Partial<ErrorResponsePayload> | null;
    throw apiRequestErrorFromResponse(response, payload, "Unable to load profile analytics.");
  }

  const payload = await response.json();
  const asNumber = (value: unknown): number => {
    const number = typeof value === "number" ? value : Number(value);
    return Number.isFinite(number) && number >= 0 ? number : 0;
  };
  const mapWeek = (entry: any): import("./models").ProfileAnalyticsWeek => ({
    week: String(entry?.week ?? entry?.weekKey ?? entry?.label ?? ""),
    totalTokens: asNumber(entry?.totalTokens ?? entry?.total_tokens ?? entry?.tokens ?? entry?.used),
  });
  const peakWeekPayload = payload?.peakWeek ?? payload?.peak_week;

  return {
    lifetimeTokens: asNumber(payload?.lifetimeTokens ?? payload?.lifetime_tokens ?? payload?.lifetime),
    peakWeek: peakWeekPayload ? mapWeek(peakWeekPayload) : null,
    longestChatTokens: asNumber(
      payload?.longestChatTokens
        ?? payload?.longest_chat_tokens
        ?? payload?.longestChat?.totalTokens
        ?? payload?.longest_chat?.total_tokens,
    ),
    weekly: Array.isArray(payload?.weekly)
      ? payload.weekly.map(mapWeek).filter((entry: import("./models").ProfileAnalyticsWeek) => entry.week)
      : Array.isArray(payload?.weeks)
        ? payload.weeks.map(mapWeek).filter((entry: import("./models").ProfileAnalyticsWeek) => entry.week)
        : [],
  };
};

/**
 * Fetches any notifications from the server.
 *
 * @returns {Promise<Response | null>} A promise that resolves to the response object containing the notifications
**/
export const getNotifications = async (): Promise<Response | null> => {
  const response = await fetchWithAuth("/api/get_notifications", {
    method: "GET",
  }).then((res) => {
    return res;
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return null;
  });

  return response;
}



/****************************************************************************************************************************************************************************************************************************
 * CONVERSATIONS
/***************************************************************************************************************************************************************************************************************************/

/**
 * Fetches the conversations for the user
 *
 * @param {number} offset - The offset for pagination
 * @returns {Promise<Conversation[] | null>} A promise that resolves to the response object containing the conversations
**/
/**
 * Maps a raw conversation payload from the API into a Conversation model.
**/
const mapConversation = (conv: any): Conversation => {
  const conversation: Conversation = {
    id: conv.id,
    title: conv.title,
    date: conv.updatedAt,
    model: chatModels.find(model => model.deployment === conv.deployment) || (chatModels.find(m => m.default) || chatModels[0]),
    reasoning: conv.reasoning || '',
    customGPTID: conv.customGPTID || '',
    selected_tool: conv.selectedTool || null,
    pinned: conv.pinned || 0,
    messages: [],
    files: conv.files || '',
    filename: conv.filename || '',
    direct_files: conv.directFiles || false,
    migrated: (!isEmpty(conv.topic) && conv.topic !== "General" && conv.topic !== "Image Generation") ? true : false,
    shared: conv.shared || 0
  };
  if (!isEmpty(conv.topic) && conv.topic === "Image Generation") conversation.model = chatModels.find(model => model.deployment === "gpt-image-1") || (chatModels.find(m => m.default) || chatModels[0]);
  return conversation;
}

export const getConversations = async (offset=0): Promise<Conversation[] | null> => {
  const response = await fetchWithAuth(`/api/conversations/load?offset=${offset}`, {
    method: "GET",
  }).then(async (res) => {
    const payload = await res.json();
    return payload.map(mapConversation) as Conversation[];
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return null;
  });

  return response;
}

/**
 * Fetches all pinned conversations for the user (not paginated).
 *
 * Pinned chats must always be visible regardless of age, so they are loaded in
 * full rather than through the paged /load endpoint.
 *
 * @returns {Promise<Conversation[] | null>} A promise that resolves to the user's pinned conversations
**/
export const getPinnedConversations = async (): Promise<Conversation[] | null> => {
  const response = await fetchWithAuth(`/api/conversations/pinned`, {
    method: "GET",
  }).then(async (res) => {
    const payload = await res.json();
    return payload.map(mapConversation) as Conversation[];
  }).catch((err) => {
    console.error("There was an issue fetching your pinned conversations.");
    return null;
  });

  return response;
}

/**
 * Fetch a specified conversation
 *
 * @param {string} convId - The ID of the conversation to fetch
 * @param {boolean} shared - Whether the conversation is shared
 * @returns {Promise<Conversation | null>} A promise that resolves to a Conversation object
**/
export const getConversation = async (convId: string, shared: boolean): Promise<Conversation | null> => {
  const response = await fetchWithAuth("/api/conversation/load", {
    method: "POST",
    body: JSON.stringify({ conversation_id: convId, shared: shared }),
    headers: { "Content-Type": "application/json" }
  })
  .then(async (res) => {
    if (!res) return null;

    const payload = await res.json();
    let messages: ChatMessage[] = [];
    payload.messages.forEach((msg: any) => {
      const message: ChatMessage = {
        id: msg.id,
        role: msg.role,
        date: msg.createdAt,
        content: msg.content || '',
        anchorMessageId: msg.anchorMessageId || undefined,
        context: msg.context || '',
        feedback: msg.feedback || undefined,
        model: msg.model || '',
        files: msg.files || '',
        filename: msg.filename || '',
        direct_files: msg.directFiles || false,
        publicImage: msg.publicImage || false,
      }
      messages.push(message)
    });
    messages = normalizeCompactionTimeline(messages, { dropOrphans: true });
    
    const conversation: Conversation = {
      id: payload?.conversation.id,
      title: payload?.conversation.title,
      date: payload?.conversation.createdAt,
      model: chatModels.find(model => model.deployment === payload?.conversation.deployment) || (chatModels.find(m => m.default) || chatModels[0]),
      reasoning: payload?.conversation.reasoning || '',
      customGPTID: payload?.conversation.customGPTID || '',
      selected_tool: payload?.conversation.selectedTool || null,
      pinned: payload?.conversation.pinned || 0,
      messages: messages,
      presentations: Array.isArray(payload.presentations)
        ? (payload.presentations as PresentationAsset[]).map((presentation) => ({
            ...presentation,
            filename: getPresentationFilename(payload?.conversation?.title, presentation.filename),
          }))
        : [],
      files: payload?.conversation.files || '',
      filename: payload?.conversation.filename || '',
      direct_files: payload?.conversation.directFiles || false,
      migrated: (!isEmpty(payload?.conversation.topic) && payload?.conversation.topic !== "General" && payload?.conversation.topic !== "Image Generation") ? true : false,
      shared: payload?.conversation.shared || 0
    };
    return conversation;
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return null;
  });

  return response;
}

/**
 * Fetches the SAS URL for a storage blob (file)
 *
 * @param {string} blob - The blob identifier
 * @param {string} blob_name - The blob container
 * @returns {Promise<any>} A promise that resolves to a JSON response
**/
export const getSasUrl = async (blob: string, blob_name: string): Promise<any> => {
  const config = {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      blob: blob,
      blob_name: blob_name
    })
  };

  return fetchWithAuth("/api/conversation/sas", config).then((res) => res.json());
}

/**
 * Fetches the SAS URL for a blob reference
 *
 * @param {string} blob_reference - The blob reference
 * @returns {Promise<any>} A promise that resolves to a JSON response
**/
export const getImageSas = async (blob_reference: string): Promise<any> => {
  const config = {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      blob_reference: blob_reference
    })
  };

  return fetchWithAuth("/api/conversation/image-sas", config).then((res) => res.json());
}

/**
 * Fetches an image blob from the server (proxied through backend to avoid CORS)
 *
 * @param {string} imageUrl - The URL of the image to fetch
 * @returns {Promise<Blob>} A promise that resolves to the image blob
**/
export const getImageBlob = async (imageUrl: string): Promise<Blob> => {
  const config = {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      image_url: imageUrl
    })
  };

  const response = await fetchWithAuth("/api/conversation/image-blob", config);
  if (!response.ok) throw new Error('Failed to fetch image');
  return response.blob();
}

export interface UploadTransferProgress {
  /** Bytes written to the wire so far, including multipart overhead. */
  sent: number;
  /** Total wire bytes for the whole request body. */
  total: number;
}

/**
 * Sends the uploaded file to the server to be stored and processed.
 *
 * Uses XMLHttpRequest rather than fetch specifically for `upload.onprogress` —
 * fetch has no way to report how much of the request body has been sent, which is
 * the slow half of a large upload. The result is normalised back to a Response so
 * callers keep reading `.status` and `.json()` as before, and transport failures
 * resolve to null exactly like the old `.catch(() => null)` did.
 *
 * @param {FormData} form - The form data containing the file to be uploaded
 * @param {function} [onTransferProgress] - Called as request-body bytes are sent
 * @param {function} [onTransferComplete] - Called once the request body is fully sent,
 *   which is where the server-side wait begins — the promise itself does not settle
 *   until the server has finished and responded.
 * @returns {Promise<Response | null>} A promise that resolves to the response object
**/
export const fileUploadAndIndex = async (
  form: FormData,
  onTransferProgress?: (progress: UploadTransferProgress) => void,
  onTransferComplete?: () => void
): Promise<Response | null> => {
  let authHeaders: Record<string, string>;
  try {
    authHeaders = await resolveAuthHeaders();
  } catch {
    return null;
  }

  return new Promise<Response | null>((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/conversation/file");
    xhr.responseType = "blob";
    Object.entries(authHeaders).forEach(([name, value]) => xhr.setRequestHeader(name, value));

    if (onTransferProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onTransferProgress({ sent: event.loaded, total: event.total });
      };
    }
    // Fires even when progress was not length-computable, so this — not the last
    // onprogress event — is the dependable end-of-transfer signal.
    if (onTransferComplete) xhr.upload.onload = () => onTransferComplete();

    xhr.onload = () => {
      try {
        // 204/205 must not carry a body, and a status of 0 is not constructible —
        // both throw out of the Response constructor, which the executor would
        // swallow, leaving this promise pending forever.
        const body = xhr.status === 204 || xhr.status === 205 ? null : xhr.response;
        resolve(new Response(body, { status: xhr.status, statusText: xhr.statusText }));
      } catch {
        resolve(null);
      }
    };
    xhr.onerror = () => resolve(null);
    xhr.onabort = () => resolve(null);
    xhr.ontimeout = () => resolve(null);

    xhr.send(form);
  });
}

/**
 * Re-runs full OCR on all files attached to a message (forces the advanced
 * processing path), overwrites each file's block in the saved message context,
 * and returns the updated full message context.
 *
 * @param conversationId  Conversation the files belong to
 * @param messageId       The user message whose context holds the files
 * @param filenames       The files to reprocess
 * @returns {Promise<Response | null>} resolves to the response, or null on network error
 */
export const reprocessFile = async (conversationId: string, messageId: string, filenames: string[]): Promise<Response | null> => {
  const config = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ conversation_id: conversationId, message_id: messageId, filenames }),
  };
  return fetchWithAuth('/api/conversation/file/reprocess', config).then((res) => res).catch(() => null);
};

/**
 * Fetches the status of the individual file upload processing
 *
 * @param {string} statusURL - The URL to check the status of the file upload
 * @returns {Promise<Response | null>} A promise that resolves to the response object
**/
export const checkStatus = async (statusURL: string): Promise<Response | null> => {
  const config = {
    method: "POST",
    body: JSON.stringify({ statusURL: statusURL }),
    headers: { "Content-Type": "application/json" }
  };
  return fetchWithAuth("/api/conversation/status", config).then((res) => res).catch((err) => null);
}

/**
 * Sends the uploaded image to the server to be stored and processed
 *
 * @param {FormData} form - The form data containing the image to be uploaded
 * @returns {Promise<Response | null>} A promise that resolves to the response object
**/
export const imageUpload = async (form: FormData): Promise<Response | null> => {
  const config = {
    method: "POST",
    body: form
  };
  return fetchWithAuth("/api/conversation/image", config).then((res) => res).catch((err) => null);
}

/**
 * Send the spoken audio to be transcribed
 *
 * @param {FormData} form - The form data containing the audio to be transcribed
 * @returns {Promise<Response | null>} A promise that resolves to the response object
**/
export const transcribe = async (form: FormData): Promise<Response | null> => {
  const config = {
    method: "POST",
    body: form,
  };
  return fetchWithAuth("/api/conversation/transcribe", config).then((res) => res).catch((err) => null);
}


export const createRealtimeClientSecret = async (payload: Record<string, any>): Promise<RealtimeClientSecretResponse | null> => {
  const response = await fetchWithAuth("/api/conversation/realtime/client_secret", {
    method: "POST",
    body: JSON.stringify(payload),
    headers: { "Content-Type": "application/json" }
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as Partial<ErrorResponsePayload> | null;
    throw apiRequestErrorFromResponse(response, payload, "Failed to create realtime session.");
  }
  return await response.json() as RealtimeClientSecretResponse;
}

export type RealtimePrepareTurnQuotaError = {
  error: string;
  error_code: 'weekly_token_limit_reached';
  error_type: 'quota';
  limit?: number;
  week?: string;
  error_details?: StreamErrorDetails;
};

export type RealtimePrepareTurnError = {
  error: string;
  error_code?: string;
  error_type?: string;
  status_code?: number;
  error_details?: StreamErrorDetails;
};

export const prepareRealtimeTurn = async (payload: Record<string, any>): Promise<RealtimePrepareTurnResponse | RealtimePrepareTurnError> => {
  return fetchWithAuth("/api/conversation/realtime/prepare_turn", {
    method: "POST",
    body: JSON.stringify(payload),
    headers: { "Content-Type": "application/json" }
  })
  .then(async (res) => {
    if (!res.ok) {
      const responseJson = await res.json().catch(() => null);
      if (responseJson?.error_code === 'weekly_token_limit_reached') {
        return {
          error: typeof responseJson.error === 'string'
            ? responseJson.error
            : 'Weekly token limit reached. Resets Monday 00:00 UTC.',
          error_code: 'weekly_token_limit_reached',
          error_type: 'quota',
          limit: responseJson.limit,
          week: responseJson.week,
          error_details: responseJson.error_details,
        } satisfies RealtimePrepareTurnQuotaError;
      }
      throw apiRequestErrorFromResponse(res, responseJson, "Failed to prepare realtime turn.");
    }
    return await res.json() as RealtimePrepareTurnResponse;
  })
  .catch((err) => {
    console.error("There was an issue preparing a realtime turn.", err);
    return {
      error: err instanceof Error ? err.message : "Failed to prepare realtime turn.",
      error_details: getErrorDetails(err),
      status_code: err instanceof Error && "statusCode" in err && typeof err.statusCode === "number" ? err.statusCode : undefined,
      error_code: err instanceof Error && "errorCode" in err && typeof err.errorCode === "string" ? err.errorCode : undefined,
      error_type: err instanceof Error && "errorType" in err && typeof err.errorType === "string" ? err.errorType : undefined,
    };
  });
}

export const executeRealtimeToolCall = async (payload: Record<string, any>): Promise<any> => {
  const response = await fetchWithAuth("/api/conversation/realtime/tool", {
    method: "POST",
    body: JSON.stringify(payload),
    headers: { "Content-Type": "application/json" }
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as Partial<ErrorResponsePayload> | null;
    throw apiRequestErrorFromResponse(response, payload, "Tool call failed.");
  }
  return await response.json();
}

export const persistRealtimeTurn = async (payload: Record<string, any>): Promise<PersistedRealtimeTurnResponse> => {
  return fetchWithAuth("/api/conversation/realtime/persist_turn", {
    method: "POST",
    body: JSON.stringify(payload),
    headers: { "Content-Type": "application/json" }
  })
  .then(async (res) => {
    if (!res.ok) {
      const responseJson = await res.json().catch(() => null) as Partial<ErrorResponsePayload> | null;
      throw apiRequestErrorFromResponse(res, responseJson, "Failed to persist realtime turn.");
    }
    const body = await res.json();
    const messages = normalizeCompactionTimeline((body.messages || []).map((msg: any) => ({
      id: msg.id,
      role: msg.role,
      date: msg.createdAt,
      content: msg.content || '',
      anchorMessageId: msg.anchorMessageId || undefined,
      context: msg.context || '',
      feedback: msg.feedback || undefined,
      model: msg.model || '',
      files: msg.files || '',
      filename: msg.filename || '',
      direct_files: msg.directFiles || false,
      publicImage: msg.publicImage || false,
    })), { dropOrphans: true });

    const conversation: Conversation = {
      id: body.conversation.id,
      title: body.conversation.title,
      date: body.conversation.createdAt,
      model: chatModels.find(model => model.deployment === body.conversation.deployment) || (chatModels.find(m => m.default) || chatModels[0]),
      reasoning: body.conversation.reasoning || '',
      customGPTID: body.conversation.customGPTID || '',
      selected_tool: body.conversation.selectedTool || null,
      pinned: body.conversation.pinned || 0,
      messages,
      files: body.conversation.files || '',
      filename: body.conversation.filename || '',
      direct_files: body.conversation.directFiles || false,
      migrated: (!isEmpty(body.conversation.topic) && body.conversation.topic !== "General" && body.conversation.topic !== "Image Generation") ? true : false,
      shared: body.conversation.shared || 0
    };

    return { conversation, messages };
  })
  .catch((err) => {
    console.error("There was an issue persisting a realtime turn.", err);
    throw err;
  });
}
/**
 * Initiates a Marketing Graph RAG search operation
 *
 * @param {string} prompt - The prompt for the marketing search
 * @returns {Promise<Response>} A promise that resolves to the response object
**/
export const marketingSearch = async (prompt: string): Promise<Response> => {
  const response = await fetchWithAuth("/api/conversation/marketing_search", {
    method: "POST",
    body: JSON.stringify({
      prompt: prompt
    }),
    headers: {
      "Content-Type": "application/json"
    },
  })
  .then(async (res) => {
    console.log(res)
    return res;
  })
  .catch((err) => {
    console.error("There was an issue updating.");
    let errRes: Response = {
      ...new Response,
      ok: false,
      status: 500,
    };
    return errRes;
  });

  return response;
}

/**
 * Fetches the status of the Marketing Graph RAGH search
 *
 * @param {string} guid - The GUID for the marketing search operation
 * @returns {Promise<Response | null>} A promise that resolves to the response object
**/
export const checkMarketingStatus = async (guid: string): Promise<Response | null> => {
  const config = {
    method: "POST",
    body: JSON.stringify({ guid: guid }),
    headers: { "Content-Type": "application/json" }
  };
  return fetchWithAuth("/api/conversation/marketing_status", config).then((res) => res).catch((err) => null);
}

/**
 * Sends user's prompt to server to generate a response from a model
 *
 * @param {ConversationRequest} request - The conversation request containing the messages
 * @param {ChatModel | null} currentModel - The current chat model being used
 * @param {CustomGPT | null} currentCustomGPT - The current Custom GPT being used
 * @param {Agent | null} currentAgent - The current Agent being used
 * @param {AbortSignal} abortSignal - The abort signal to cancel the request if needed
 * @param {boolean} initialFile - Whether this is the initial file upload
 * @param {boolean} addedFile - Whether a file has been added
 * @param {string} directFiles -  Whether any uploaded files were directly processed.
 * @param {string} convId - The ID of the conversation
 * @param {string} filesStr - The string of files associated with the conversation
 * @param {string} filename - The name of the file associated with the conversation
 * @param {string} imageBlobURL - The URL of the image blob associated with the conversation
 * @param {string} selectedTool - The selected tool for the conversation
 * @param {string} imageSize - The size of the image
 * @param {string} imageStyle - The style of the image
 * @param {string} marketingSearchLevel - The level of the marketing search
 * @param {string} marketingGuid - The GUID for the marketing search
 * @param {boolean} continuation - Whether this is a continuation of a previous generation
 * @param {string} timezone - The user's current time zone
 * @returns {Promise<Response>} A promise that resolves to the response object
**/
export const generate = async (request: ConversationRequest, currentModel: ChatModel | null, currentCustomGPT: CustomGPT | null, currentAgent: Agent | null, abortSignal: AbortSignal, initialFile: boolean = false, addedFile: boolean = false, directFiles: boolean = false, convId?: string, filesStr?: string, filename?: string, imageBlobURL?: string, selectedTool?: string, imageSize?: string, imageStyle?: string, marketingSearchLevel?: string, marketingGuid?: string, continuation?: boolean, timezone?: string, personalization?: Personalization): Promise<Response> => {
  let body;
  if (convId) {
    body = JSON.stringify({
      conversation_id: convId,
      currentModel: currentModel,
      currentCustomGPT: currentCustomGPT,
      currentAgent: currentAgent,
      messages: request.messages,
      initial_file: initialFile,
      added_file: addedFile,
      direct_files: directFiles,
      files_str: filesStr,
      filename: filename,
      image_blob_url: imageBlobURL,
      selected_tool: selectedTool,
      // Out-of-band force-generate signal for the Create Image button. Kept out of the
      // message body so it never pollutes multi-turn context (see ImageRoutingRedesign 4d).
      force_generate: selectedTool === 'image_generation',
      image_size: imageSize,
      image_style: imageStyle,
      marketing_search_level: marketingSearchLevel,
      marketing_guid: marketingGuid,
      continuation: continuation,
      timezone: timezone,
      personalization: personalization || DEFAULT_PERSONALIZATION,
    });
  }
  else {
    body = JSON.stringify({
      currentModel: currentModel,
      currentCustomGPT: currentCustomGPT,
      currentAgent: currentAgent,
      messages: request.messages,
      selected_tool: selectedTool,
      force_generate: selectedTool === 'image_generation',
      image_size: imageSize,
      image_style: imageStyle,
      marketing_search_level: marketingSearchLevel,
      marketing_guid: marketingGuid,
      timezone: timezone,
      personalization: personalization || DEFAULT_PERSONALIZATION,
    });
  }
  const response = await fetchWithAuth("/api/conversation/generate", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: body,
    signal: abortSignal
  })
  .then((res) => {
    return res;
  })
  .catch((err) => {
    console.error("There was an issue generating.");
    console.log(err);
    return new Response;
  });

  return response;
}

/**
* Updates the pinned status of the specified conversation
*
* @param {string} convId - The ID of the conversation to update
* @param {number} pinned - The pinned status to set for the conversation
* @returns {Promise<Response>} A promise that resolves to the response object
**/
export const pinConversation = async (convId: string, pinned: number) : Promise<Response> => {
  const response = await fetchWithAuth("/api/conversation/pin", {
    method: "POST",
    body: JSON.stringify({
      conversation_id: convId,
      pinned: pinned
    }),
    headers: {
      "Content-Type": "application/json"
    },
  })
  .then((res) => {
    return res;
  })
  .catch((err) => {
    console.error("There was an issue pinning.");
    let errRes: Response = {
      ...new Response,
      ok: false,
      status: 500,
    }
    return errRes;
  });

  return response;
}

/**
 * Updates the shared status of the specified conversation
 *
 * @param {string} convId - The ID of the conversation to update
 * @returns {Promise<Response>} A promise that resolves to the response object
**/
export const shareConversation = async (convId: string) : Promise<Response> => {
  const response = await fetchWithAuth("/api/conversation/share", {
    method: "POST",
    body: JSON.stringify({
      conversation_id: convId
    }),
    headers: {
      "Content-Type": "application/json"
    },
  })
  .then((res) => {
    return res;
  })
  .catch((err) => {
    console.error("There was an issue pinning.");
    let errRes: Response = {
      ...new Response,
      ok: false,
      status: 500,
    }
    return errRes;
  });

  return response;
}

/**
 * Updates the title of the specified conversation
 *
 * @param {string} convId - The ID of the conversation to update
 * @param {string} title - The new title for the conversation
 * @returns {Promise<Response>} A promise that resolves to the response object
**/
export const renameConversation = async (convId: string, title: string) : Promise<Response> => {
  const response = await fetchWithAuth("/api/conversation/rename", {
    method: "POST",
    body: JSON.stringify({
      conversation_id: convId,
      title: title
    }),
    headers: {
      "Content-Type": "application/json"
    },
  })
  .then((res) => {
    return res;
  })
  .catch((err) => {
    console.error("There was an issue renaming.");
    let errRes: Response = {
      ...new Response,
      ok: false,
      status: 500,
    }
    return errRes;
  });

  return response;
}

/**
 * Creates a PowerPoint presentation from the conversation
 *
 * @param {string} convId - The ID of the conversation
 * @param {boolean} createAiImages - Whether to create AI-generated images
 * @returns {Promise<Response>} A promise that resolves to the response object
**/
export const createPowerpoint = async (convId: string, createAiImages: boolean, slidesJson?: import("./models").SlideStructure | null) : Promise<Response> => {
  const response = await fetchWithAuth("/api/conversation/createPowerpoint", {
    method: "POST",
    body: JSON.stringify({
      conversation_id: convId,
      create_ai_images: createAiImages,
      slides_json: slidesJson ?? null
    }),
    headers: {
      "Content-Type": "application/json"
    },
  })
  .then((res) => {
    return res;
  })
  .catch((err) => {
    console.error("There was an issue creating PowerPoint.");
    let errRes: Response = {
      ...new Response,
      ok: false,
      status: 500,
    }
    return errRes;
  });

  return response;
}

export const checkPowerpointStatus = async (statusURL: string, presentationId?: string): Promise<Response | null> => {
  const config = {
    method: "POST",
    body: JSON.stringify({
      ...(statusURL ? { statusURL } : {}),
      ...(presentationId ? { presentation_id: presentationId } : {}),
    }),
    headers: { "Content-Type": "application/json" }
  };
  return fetchWithAuth("/api/conversation/powerpoint/status", config).then((res) => res).catch((err) => null);
}

export const getPresentationDownloadUrl = async (presentationId: string): Promise<string | null> => {
  const response = await fetchWithAuth("/api/conversation/presentation/download", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ presentation_id: presentationId }) });
  if (!response.ok) return null;
  return (await response.json()).download_url || null;
};

export const getPresentationStatus = async (presentationId: string): Promise<PresentationAsset | null> => {
  const response = await fetchWithAuth("/api/conversation/presentation/status", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ presentation_id: presentationId }) });
  if (!response.ok) return null;
  return await response.json() as PresentationAsset;
};

export const generateSlideStructure = async (convId: string, changeRequest: string = "", slidesJson?: import("./models").SlideStructure | null): Promise<Response> => {
  const response = await fetchWithAuth("/api/conversation/generateSlideStructure", {
    method: "POST",
    body: JSON.stringify({
      conversation_id: convId,
      change_request: changeRequest,
      slides_json: slidesJson ?? null
    }),
    headers: { "Content-Type": "application/json" }
  })
  .then((res) => res)
  .catch(() => {
    console.error("There was an issue generating slide structure.");
    return { ...new Response, ok: false, status: 500 } as Response;
  });
  return response;
}

export const checkSlideStructureStatus = async (statusURL: string): Promise<Response | null> => {
  const config = {
    method: "POST",
    body: JSON.stringify({ statusURL: statusURL }),
    headers: { "Content-Type": "application/json" }
  };
  return fetchWithAuth("/api/conversation/slideStructure/status", config).then((res) => res).catch(() => null);
}

/**
 * Delete specified message due to generation having been aborted
 *
 * @param {string} convId - The ID of the conversation
 * @param {string} messageId - The ID of the message to delete
 * @returns {Promise<Response>} A promise that resolves to the response object
**/
export const deleteAborted = async (convId: string, messageId: string) : Promise<Response> => {
  const response = await fetchWithAuth("/api/conversation/aborted", {
    method: "DELETE",
    body: JSON.stringify({
      conversation_id: convId,
      message_id: messageId,
    }),
    headers: { "Content-Type": "application/json" }
  })
  .then((res) => {
    return res;
  })
  .catch((err) => {
    console.error("There was an issue aborting.");
    let errRes: Response = {
      ...new Response,
      ok: false,
      status: 500,
    };
    return errRes;
  });

  return response;
}

export const deleteGroupAborted = async (convId: string, messageId: string): Promise<Response> => {
  return fetchWithAuth("/api/conversation/group/aborted", {
    method: "DELETE",
    body: JSON.stringify({ conversation_id: convId, message_id: messageId }),
    headers: { "Content-Type": "application/json" },
  }).catch(() => ({ ...new Response, ok: false, status: 500 } as Response));
};

/**
 * Delete specified conversation
 *
 * @param {string} convId - The ID of the conversation to delete
 * @returns {Promise<Response>} A promise that resolves to the response object
**/
export const deleteConversation = async (convId: string) : Promise<Response> => {
  const response = await fetchWithAuth("/api/conversation/delete", {
    method: "DELETE",
    body: JSON.stringify({
      conversation_id: convId,
    }),
    headers: {
      "Content-Type": "application/json"
    }
  })
  .then((res) => {
    return res;
  })
  .catch((err) => {
    console.error("There was an issue deleting.");
    let errRes: Response = {
      ...new Response,
      ok: false,
      status: 500,
    }
    return errRes;
  });
  
  return response;
}

/**
 * Save message feedback
 *
 * @param {string} messageId - The ID of the message to save feedback for
 * @param {string} feedback - The feedback to save
 * @returns {Promise<Response>} A promise that resolves to the response object
**/
export const saveMessageFeedback = async (messageId: string, feedback: string): Promise<Response> => {
  const response = await fetchWithAuth("/api/conversation/feedback/save", {
    method: "POST",
    body: JSON.stringify({
      message_id: messageId,
      message_feedback: feedback
    }),
    headers: { "Content-Type": "application/json" }
  })
  .then((res) => {
    return res;
  })
  .catch((err) => {
    console.error("There was an issue logging feedback.");
    let errRes: Response = {
      ...new Response,
      ok: false,
      status: 500,
    }
    return errRes;
  });

  return response;
}

/**
 * Delete all conversations
 *
 * @returns {Promise<Response>} A promise that resolves to the response object
**/
export const deleteConversations = async () : Promise<Response> => {
  const response = await fetchWithAuth("/api/conversations/delete", {
    method: "DELETE",
    body: JSON.stringify({}),
    headers: { "Content-Type": "application/json" }
  })
  .then((res) => {
    return res;
  })
  .catch((err) => {
    console.error("There was an issue deleting all.");
    let errRes: Response = {
      ...new Response,
      ok: false,
      status: 500,
    };
    return errRes;
  });

  return response;
}

/**
 * Sending images library warning
 * 
 * @param {string} conversationId - The ID of the conversation
 * @param {string} userId - The ID of the user
 * @returns {Promise<Response>} A promise that resolves to the response object
**/
export const sendImageWarning = async (conversationId: string, userId: string): Promise<Response> => {
  const response = await fetchWithAuth("/api/image/warning", {
    method: "POST",
    body: JSON.stringify({
      conversation_id: conversationId,
      user_id: userId
    }),
    headers: { "Content-Type": "application/json" }
  })
  .then((res) => {
    return res;
  })
  .catch((err) => {
    console.error("There was an issue sending image warning.");
    return new Response;
  });

  return response;
}

/**
 * Fetch a search conversations
 *
 * @param {string} search - The search string
 * @returns {Promise<SearchConversation[]>} A promise that resolves to a SearchConversation object
**/
export const getSearchConversations = async (search: string): Promise<SearchConversation[]> => {
  const response = await fetchWithAuth("/api/search", {
    method: "POST",
    body: JSON.stringify({ search: search }),
    headers: { "Content-Type": "application/json" }
  })
  .then(async (res) => {
    if (!res) return [];

    const payload = await res.json();
    let conversations: SearchConversation[] = [];
    payload.forEach((conv: any) => {
      const conversation: SearchConversation = {
        id: conv.id,
        title: conv.title,
        pinned: conv.pinned || 0,
        date: conv.date,
      }
      conversations.push(conversation)
    });
    return conversations;
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return [];
  });

  return response;
}



/****************************************************************************************************************************************************************************************************************************
 * CUSTOM GPTS
/***************************************************************************************************************************************************************************************************************************/

/**
 * Check Custom GPTs for similar ones that already exist
 *
 * @param {string} name - The name of the Custom GPT to check
 * @param {string} description - The description of the Custom GPT to check
 * @returns {Promise<any>} A promise that resolves
**/
export const checkCustomGPT = async (name: string, description: string): Promise<any> => {
  const response = await fetchWithAuth("/api/customgpt/check", {
    method: "POST",
    body: JSON.stringify({ name: name, description: description }),
    headers: { "Content-Type": "application/json" }
  }).then(async (res) => {
    const payload = await res.json();
    return payload;
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return [];
  });

  return response;
}

/**
 * Save Custom GPT details
 *
 * @param {FormData} form - The form data containing the Custom GPT details
 * @returns {Promise<Response>} A promise that resolves to the response object
**/
export const saveCustomGPT = async (form: FormData): Promise<Response> => {
  const response = await fetchWithAuth("/api/customgpt/save", {
    method: "POST",
    body: form
  })
  .then((res) => {
    return res;
  })
  .catch((err) => {
    console.error("There was an issue saving.");
    return new Response(null, { status: 500 });
  });

  return response;
}

/**
 * Get all Custom GPTs
 *
 * @param {string} searchText - The search text to filter Custom GPTs
 * @param {number} creator - The creator ID to filter Custom GPTs
 * @returns {Promise<CustomGPT[]>} A promise that resolves to the response object
**/
export const getAllCustomGPTs = async (searchText: string, creator: number): Promise<CustomGPT[]> => {
  const response = await fetchWithAuth("/api/customgpts/load", {
    method: "POST",
    body: JSON.stringify({
      searchText: searchText,
      creator: creator
    }),
    headers: {
      "Content-Type": "application/json"
    }
  }).then(async (res) => {
    const payload = await res.json();
    
    const customGPTs: CustomGPT[] = await Promise.all(payload.map(async (cgpt: any) => {
      const customGPT: CustomGPT = {
        id: cgpt.id,
        image_path: cgpt.filename,
        name: cgpt.name,
        description: cgpt.description,
        category: cgpt.category || '',
        system_prompt: cgpt.systemPrompt,
        conv_starter1: cgpt.convStarter1 || '',
        conv_starter2: cgpt.convStarter2 || '',
        conv_starter3: cgpt.convStarter3 || '',
        conv_starter4: cgpt.convStarter4 || '',
        default_model: cgpt.model || '',
        has_knowledge: cgpt.hasKnowledge,
        sharepointURL: cgpt.sharepointURL,
        sharepointURLs: cgpt.sharepointURLs || (cgpt.sharepointURL ? [cgpt.sharepointURL] : []),
        sharepointArticles: cgpt.sharepointArticles || '',
        top_n: cgpt.topN || '10',
        files_downloadable: cgpt.filesDownloadable || 1,
        visibility: cgpt.visibility || 0,
        ready: (cgpt.ready == 'true' ? true : false),
        rating: cgpt.rating || 0,
        rating_count: cgpt.ratingCount || 0,
        conversation_count: cgpt.conversationCount || 0,
        created_by: cgpt.createdBy,
        created_by_name: cgpt.createdByName,
        co_creator: cgpt.coCreator || '',
        co_creator_name: cgpt.coCreatorName || '',
        pinned: cgpt.pinned || false
      };
      return customGPT;
    }));
    return customGPTs;
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return [];
  });

  return response;
}

/**
 * Get Custom GPT's status
 *
 * @param {string} id - The ID of the Custom GPT to get status for
 * @returns {Promise<any>} A promise that resolves
**/
export const getCustomGPTStatus = async (id: string): Promise<any> => {
  const response = await fetchWithAuth("/api/customgpt/status", {
    method: "POST",
    body: JSON.stringify({ id: id }),
    headers: { "Content-Type": "application/json" }
  }).then(async (res) => {
    const payload = await res.json();
    return payload;
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return [];
  });

  return response;
}

/**
 * Generate system prompt to suggest for Custom GPT
 *
 * @param {string} name - The name of the Custom GPT
 * @param {string} description - The description of the Custom GPT
 * @returns {Promise<any>} A promise that resolves
**/
export const generateCustomGPTSystemPrompt = async (name: string, description: string): Promise<any> => {
  const response = await fetchWithAuth("/api/customgpt/generate_system_prompt", {
    method: "POST",
    body: JSON.stringify({ name: name, description: description }),
    headers: { "Content-Type": "application/json" }
  }).then(async (res) => {
    const payload = await res.json();
    return payload;
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return [];
  });

  return response;
}

/**
 * Resync Custom GPT
 *
 * @param {string} id - The ID of the Custom GPT to resync
 * @returns {Promise<Response>} A promise that resolves to the response object
**/
export const resyncCustomGPT = async (id: string): Promise<Response> => {
  const response = await fetchWithAuth("/api/customgpt/resync", {
    method: "POST",
    body: JSON.stringify({
      id: id
    }),
    headers: { "Content-Type": "application/json" }
  })
  .then((res) => {
    return res;
  })
  .catch((err) => {
    console.error("There was an issue resyncing.");
    return new Response;
  });

  return response;
}

/**
 * Delete Custom GPT
 *
 * @param {string} id - The ID of the Custom GPT to delete
 * @returns {Promise<Response>} A promise that resolves to the response object
**/
export const deleteCustomGPT = async (id: string): Promise<Response> => {
  const response = await fetchWithAuth("/api/customgpt/delete", {
    method: "POST",
    body: JSON.stringify({
      id: id
    }),
    headers: { "Content-Type": "application/json" }
  })
  .then((res) => {
    return res;
  })
  .catch((err) => {
    console.error("There was an issue deleting.");
    return new Response;
  });

  return response;
}

/**
 * Add access request for the selected Custom GPT for the user
 * 
 * @param {string} customGPTID - The ID of the Custom GPT
 * @param {string} userId - The ID of the user
 * @param {string} name - The name of the user
 * @param {number} status - The status of the access request
 * @param {number} source - The source type (0 for group, 1 for user)
 * @returns {Promise<void>} A promise that resolves
**/
export const addCustomGPTAccess = async (customGPTID: string, userId: string, name: string, status: number, source: number): Promise<void> => {
  await fetchWithAuth("/api/customgpt/access/add", {
    method: "POST",
    body: JSON.stringify({
      customGPTID: customGPTID,
      userId: userId,
      name: name,
      status: status,
      source: source
    }),
    headers: {
      "Content-Type": "application/json"
    }
  });
}

/**
 * Grant or revoke access request for the selected Custom GPT for the user
 * 
 * @param {string} id - The ID of the access request
 * @param {number} status - The status to set (1 for grant, 0 for revoke)
 * @returns {Promise<void>} A promise that resolves
**/
export const grantCustomGPTAccess = async (id: string, status: number): Promise<void> => {
  await fetchWithAuth("/api/customgpt/access/grant", {
    method: "POST",
    body: JSON.stringify({
      id: id,
      status: status
    }),
    headers: {
      "Content-Type": "application/json"
    }
  });
}

/**
 * Add the selected Custom GPT for the current user
 * 
 * @param {CustomGPT} customGPT - The Custom GPT to add
 * @returns {Promise<void>} A promise that resolves
**/
export const addCustomGPTUser = async (customGPT: CustomGPT): Promise<void> => {
  await fetchWithAuth("/api/customgpt/user/add", {
    method: "POST",
    body: JSON.stringify({
      customGPT: customGPT
    }),
    headers: {
      "Content-Type": "application/json"
    }
  });
}

/**
 * Remove the selected Custom GPT for the current user
 * 
 * @param {CustomGPT} customGPT - The Custom GPT to remove
 * @returns {Promise<void>} A promise that resolves
**/
export const removeCustomGPTUser = async (customGPT: CustomGPT): Promise<void> => {
  await fetchWithAuth("/api/customgpt/user/remove", {
    method: "POST",
    body: JSON.stringify({
      customGPT: customGPT
    }),
    headers: {
      "Content-Type": "application/json"
    }
  });
}

/**
 * Get the current user's Custom GPTs
 *
 * @returns {Promise<CustomGPT[]>} A promise that resolves to the response object
**/
export const getUsersCustomGPTs = async (): Promise<CustomGPT[]> => {
  const response = await fetchWithAuth("/api/customgpt/user/load", {
    method: "GET"
  }).then(async (res) => {
    const payload = await res.json();
    
    const customGPTs: CustomGPT[] = await Promise.all(payload.map(async (cgpt: any) => {
      const customGPT: CustomGPT = {
        id: cgpt.id,
        image_path: cgpt.filename,
        name: cgpt.name,
        description: cgpt.description,
        system_prompt: cgpt.systemPrompt,
        conv_starter1: cgpt.convStarter1 || '',
        conv_starter2: cgpt.convStarter2 || '',
        conv_starter3: cgpt.convStarter3 || '',
        conv_starter4: cgpt.convStarter4 || '',
        default_model: cgpt.model || '',
        has_knowledge: cgpt.hasKnowledge,
        sharepointURL: cgpt.sharepointURL,
        sharepointURLs: cgpt.sharepointURLs || (cgpt.sharepointURL ? [cgpt.sharepointURL] : []),
        top_n: cgpt.topN || '10',
        files_downloadable: cgpt.filesDownloadable || 1,
        visibility: cgpt.visibility || 0,
        ready: (cgpt.ready == 'true' ? true : false),
        rating: cgpt.rating || 0,
        rating_count: cgpt.ratingCount || 0,
        conversation_count: cgpt.conversationCount || 0,
        created_by: cgpt.createdBy,
        created_by_name: cgpt.createdByName
      };
      return customGPT;
    }));
    return customGPTs;
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return [];
  });

  return response;
}

/**
 * Get Custom GPT information
 *
 * @param {string} customGPTID - The ID of the Custom GPT
 * @returns {Promise<any>} A promise that resolves to the Custom GPT information
**/
export const getCustomGPT = async (customGPTID: string): Promise<any> => {
  const response = await fetchWithAuth("/api/customgpt/load", {
    method: "POST",
    body: JSON.stringify({ customGPTID: customGPTID }),
    headers: { "Content-Type": "application/json" }
  }).then(async (res) => {
    const payload = await res.json();
    return payload;
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return [];
  });

  return response;
}

/**
 * Get Custom GPT object
 *
 * @param {string} customGPTID - The ID of the Custom GPT
 * @returns {Promise<any>} A promise that resolves to the Custom GPT object
**/
export const getCustomGPTObj = async (customGPTID: string): Promise<any> => {
  const response = await fetchWithAuth("/api/customgpt/load", {
    method: "POST",
    body: JSON.stringify({ customGPTID: customGPTID }),
    headers: { "Content-Type": "application/json" }
  }).then(async (res) => {
    const payload = await res.json();
    
    if (payload.customGPT) {
      const customGPTs: CustomGPT[] = await Promise.all(payload.customGPT.map(async (cgpt: any) => {
        const customGPT: CustomGPT = {
          id: cgpt.id,
          image_path: cgpt.filename,
          name: cgpt.name,
          description: cgpt.description,
          system_prompt: cgpt.systemPrompt,
          conv_starter1: cgpt.convStarter1 || '',
          conv_starter2: cgpt.convStarter2 || '',
          conv_starter3: cgpt.convStarter3 || '',
          conv_starter4: cgpt.convStarter4 || '',
          default_model: cgpt.model || '',
          has_knowledge: cgpt.hasKnowledge,
          sharepointURL: cgpt.sharepointURL,
          sharepointURLs: cgpt.sharepointURLs || (cgpt.sharepointURL ? [cgpt.sharepointURL] : []),
          top_n: cgpt.topN || '10',
          files_downloadable: cgpt.filesDownloadable || 1,
          visibility: cgpt.visibility || 0,
          ready: (cgpt.ready == 'true' ? true : false),
          rating: cgpt.rating || 0,
          rating_count: cgpt.ratingCount || 0,
          conversation_count: cgpt.conversationCount || 0,
          created_by: cgpt.createdBy,
          created_by_name: cgpt.createdByName
        };
        return customGPT;
      }));
      return customGPTs[0];
    }
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return null;
  });

  return response;
}

/**
 * Get Custom GPT information
 *
 * @param {string} customGPTName - The name of the Custom GPT
 * @returns {Promise<any>} A promise that resolves to the Custom GPT information
**/
export const getCustomGPTByName = async (customGPTName: string): Promise<any> => {
  const response = await fetchWithAuth("/api/customgpt/load", {
    method: "POST",
    body: JSON.stringify({ customGPTName: customGPTName }),
    headers: { "Content-Type": "application/json" }
  }).then(async (res) => {
    const payload = await res.json();
    
    if (payload.customGPT) {
      const customGPTs: CustomGPT[] = await Promise.all(payload.customGPT.map(async (cgpt: any) => {
        const customGPT: CustomGPT = {
          id: cgpt.id,
          image_path: cgpt.filename,
          name: cgpt.name,
          description: cgpt.description,
          system_prompt: cgpt.systemPrompt,
          conv_starter1: cgpt.convStarter1 || '',
          conv_starter2: cgpt.convStarter2 || '',
          conv_starter3: cgpt.convStarter3 || '',
          conv_starter4: cgpt.convStarter4 || '',
          default_model: cgpt.model || '',
          has_knowledge: cgpt.hasKnowledge,
          sharepointURL: cgpt.sharepointURL,
          sharepointURLs: cgpt.sharepointURLs || (cgpt.sharepointURL ? [cgpt.sharepointURL] : []),
          top_n: cgpt.topN || '10',
          files_downloadable: cgpt.filesDownloadable || 1,
          visibility: cgpt.visibility || 0,
          ready: (cgpt.ready == 'true' ? true : false),
          rating: cgpt.rating || 0,
          rating_count: cgpt.ratingCount || 0,
          conversation_count: cgpt.conversationCount || 0,
          created_by: cgpt.createdBy,
          created_by_name: cgpt.createdByName
        };
        return customGPT;
      }));
      return customGPTs[0];
    }
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return null;
  });

  return response;
}

/**
 * Get the details for a Custom GPT
 *
 * @param {string} customGPTID - The ID of the Custom GPT
 * @returns {Promise<any>} A promise that resolves to the Custom GPT details
**/
export const getCustomGPTDetails = async (customGPTID: string): Promise<any> => {
  const response = await fetchWithAuth("/api/customgpt/details/load", {
    method: "POST",
    body: JSON.stringify({ id: customGPTID }),
    headers: { "Content-Type": "application/json" }
  }).then(async (res) => {
    const payload = await res.json();
    return payload;
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return [];
  });

  return response;
}

/**
 * Get the indexed files for a Custom GPT
 *
 * @param {string} customGPTID - The ID of the Custom GPT
 * @returns {Promise<any>} A promise that resolves to the response object
**/
export const getCustomGPTIndexedFiles = async (customGPTID: string): Promise<any> => {
  const response = await fetchWithAuth("/api/customgpt/files/indexed", {
    method: "POST",
    body: JSON.stringify({ id: customGPTID }),
    headers: { "Content-Type": "application/json" }
  }).then(async (res) => {
    const payload = await res.json();
    return payload;
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return [];
  });

  return response;
}

/**
 * Get the access for a Custom GPT
 *
 * @param {string} customGPTID - The ID of the Custom GPT
 * @returns {Promise<any>} A promise that resolves to the response object
**/
export const getCustomGPTAccess = async (customGPTID: string): Promise<any> => {
  const response = await fetchWithAuth("/api/customgpt/access/load", {
    method: "POST",
    body: JSON.stringify({ customGPTID: customGPTID }),
    headers: { "Content-Type": "application/json" }
  }).then(async (res) => {
    const payload = await res.json();
    return payload;
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return [];
  });

  return response;
}

/**
 * Save rating for the Custom GPT for the current user
 * 
 * @param {CustomGPT} customGPT - The Custom GPT to rate
 * @param {number} rating - The rating to save
 * @returns {Promise<void>} A promise that resolves
**/
export const saveRating = async (customGPT: CustomGPT, rating: number): Promise<void> => {
  await fetchWithAuth("/api/customgpt/user/rate", {
    method: "POST",
    body: JSON.stringify({
      customGPT: customGPT,
      rating: rating
    }),
    headers: {
      "Content-Type": "application/json"
    }
  });
}



/****************************************************************************************************************************************************************************************************************************
 * AGENTS
/***************************************************************************************************************************************************************************************************************************/

/**
 * Check agents for similar ones that already exist
 *
 * @param {string} customGPTs - The selected Custom GPTs
 * @returns {Promise<any>} A promise that resolves
**/
export const checkAgent = async (customGPTs: string): Promise<any> => {
  const response = await fetchWithAuth("/api/agent/check", {
    method: "POST",
    body: JSON.stringify({ customGPTs: customGPTs }),
    headers: { "Content-Type": "application/json" }
  }).then(async (res) => {
    const payload = await res.json();
    return payload;
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return [];
  });

  return response;
}

/**
 * Save agent details
 *
 * @param {FormData} form - The form data containing the agent details
 * @returns {Promise<Response>} A promise that resolves to the response object
**/
export const saveAgent = async (form: FormData): Promise<Response> => {
  const response = await fetchWithAuth("/api/agent/save", {
    method: "POST",
    body: form
  })
  .then((res) => {
    return res;
  })
  .catch((err) => {
    console.error("There was an issue saving.");
    return new Response;
  });

  return response;
}

/**
 * Get all agents
 *
 * @param {string} searchText - The search text to filter agents
 * @param {number} creator - The creator ID to filter agents
 * @returns {Promise<Agent[]>} A promise that resolves to the response object
**/
export const getAllAgents = async (searchText: string, creator: number): Promise<Agent[]> => {
  const response = await fetchWithAuth("/api/agents/load", {
    method: "POST",
    body: JSON.stringify({
      searchText: searchText,
      creator: creator
    }),
    headers: {
      "Content-Type": "application/json"
    }
  }).then(async (res) => {
    const payload = await res.json();
    
    const agents: Agent[] = await Promise.all(payload.map(async (a: any) => {
      const agent: Agent = {
        id: a.id,
        type: a.type,
        name: a.name,
        description: a.description,
        customGPTs: a.customGPTs,
        created_by: a.createdBy,
        created_by_name: a.createdByName
      };
      return agent;
    }));
    return agents;
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return [];
  });

  return response;
}

/**
 * Get agent information
 *
 * @param {string} agentID - The ID of theagent
 * @returns {Promise<any>} A promise that resolves to the agent information
**/
export const getAgent = async (agentID: string): Promise<any> => {
  const response = await fetchWithAuth("/api/agent/load", {
    method: "POST",
    body: JSON.stringify({ agentID: agentID }),
    headers: { "Content-Type": "application/json" }
  }).then(async (res) => {
    const payload = await res.json();
    return payload;
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return [];
  });

  return response;
}

/**
 * Get the details for an agent
 *
 * @param {string} agentID - The ID of the agent
 * @returns {Promise<any>} A promise that resolves to the agent details
**/
export const getAgentDetails = async (agentID: string): Promise<any> => {
  const response = await fetchWithAuth("/api/agent/details/load", {
    method: "POST",
    body: JSON.stringify({ id: agentID }),
    headers: { "Content-Type": "application/json" }
  }).then(async (res) => {
    const payload = await res.json();
    return payload;
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return [];
  });

  return response;
}

/**
 * Get the current user's agents
 *
 * @returns {Promise<Agent[]>} A promise that resolves to the response object
**/
export const getUsersAgents = async (): Promise<Agent[]> => {
  const response = await fetchWithAuth("/api/agent/user/load", {
    method: "GET"
  }).then(async (res) => {
    const payload = await res.json();
    
    const agents: Agent[] = await Promise.all(payload.map(async (a: any) => {
      const agent: Agent = {
        id: a.id,
        type: a.type,
        name: a.name,
        description: a.description,
        customGPTs: a.customGPTs,
        created_by: a.createdBy,
        created_by_name: a.createdByName
      };
      return agent;
    }));
    return agents;
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return [];
  });

  return response;
}

/**
 * Add the selected agent for the current user
 * 
 * @param {Agent} agent - The agent to add
 * @returns {Promise<void>} A promise that resolves
**/
export const addAgentUser = async (agent: Agent): Promise<void> => {
  await fetchWithAuth("/api/agent/user/add", {
    method: "POST",
    body: JSON.stringify({
      agent: agent
    }),
    headers: {
      "Content-Type": "application/json"
    }
  });
}

/**
 * Remove the selected agent for the current user
 * 
 * @param {Agent} agent - The agent to remove
 * @returns {Promise<void>} A promise that resolves
**/
export const removeAgentUser = async (agent: Agent): Promise<void> => {
  await fetchWithAuth("/api/agent/user/remove", {
    method: "POST",
    body: JSON.stringify({
      agent: agent
    }),
    headers: {
      "Content-Type": "application/json"
    }
  });
}

/**
 * Delete agent
 *
 * @param {string} id - The ID of the agent to delete
 * @returns {Promise<Response>} A promise that resolves to the response object
**/
export const deleteAgent = async (id: string): Promise<Response> => {
  const response = await fetchWithAuth("/api/agent/delete", {
    method: "POST",
    body: JSON.stringify({
      id: id
    }),
    headers: { "Content-Type": "application/json" }
  })
  .then((res) => {
    return res;
  })
  .catch((err) => {
    console.error("There was an issue deleting.");
    return new Response;
  });

  return response;
}



/****************************************************************************************************************************************************************************************************************************
 * IMAGES LIBRARY
/***************************************************************************************************************************************************************************************************************************/

/**
 * Add image response to Public Images Library
 *
 * @param {string} convId - The ID of the conversation
 * @param {string} msgId - The ID of the message
 * @param {string} prompt - The prompt for the image
 * @param {string} url - The URL of the image
 * @returns {Promise<Response>} A promise that resolves to the response object
**/
export const addImageToPublicImages = async (convId: string, msgId: String, prompt: string, url: string): Promise<Response> => {
  const response = await fetchWithAuth("/api/public-images/add", {
    method: "POST",
    body: JSON.stringify({
      conversation_id: convId,
      message_id: msgId,
      prompt: prompt,
      url: url
    }),
    headers: { "Content-Type": "application/json" }
  })
  .then((res) => {
    return res;
  })
  .catch((err) => {
    console.error("There was an issue saving.");
    return new Response;
  });

  return response;
}

/**
 * Remove image response from Public Images Library
 *
 * @param {string} convId - The ID of the conversation
 * @param {string} msgId - The ID of the message
 * @returns {Promise<Response>} A promise that resolves to the response object
**/
export const removeImageFromPublicImages = async (convId: string, msgId: String): Promise<Response> => {
  const response = await fetchWithAuth("/api/public-images/remove", {
    method: "POST",
    body: JSON.stringify({
      conversation_id: convId,
      message_id: msgId
    }),
    headers: { "Content-Type": "application/json" }
  })
  .then((res) => {
    return res;
  })
  .catch((err) => {
    console.error("There was an issue saving.");
    return new Response;
  });

  return response;
}

/**
 * Get all public image library images
 *
 * @returns {Promise<LibraryImage[]>} A promise that resolves to the response object
**/
export const getPublicImages = async (searchText: string = ''): Promise<LibraryImage[]> => {
  const response = await fetchWithAuth(`/api/public-images?searchText=${encodeURIComponent(searchText)}`, {
    method: "GET"
  }).then(async (res) => {
    const payload = await res.json();
    
    const images: LibraryImage[] = await Promise.all(payload.map(async (img: any) => {
      const image: LibraryImage = {
        id: img.id,
        conversationId: img.conversationId,
        messageId: img.messageId,
        prompt: img.prompt,
        imageUrl: img.imageUrl,
        addedByName: img.addedByName,
        createdAt: img.createdAt,
        userId: img.userId
      };
      return image;
    }));
    return images;
  }).catch((err) => {
    console.error("There was an issue fetching your data.");
    return [];
  });

  return response;
}

export type PersonalLibraryPage = {
  items: PersonalLibraryItem[];
  nextCursor: string | null;
  hasMore: boolean;
};

const mapPersonalLibraryItem = (item: any): PersonalLibraryItem => {
  const rawKind = item.kind || item.type;
  const kind: PersonalLibraryKind = rawKind === "file" || rawKind === "uploaded_image" || rawKind === "generated_image" || rawKind === "presentation"
    ? rawKind
    : rawKind === "uploaded" ? "uploaded_image" : "generated_image";
  return {
    id: String(item.id),
    kind,
    url: item.url || item.imageUrl || item.downloadUrl || item.sasUrl || item.sas_url || null,
    filename: kind === "presentation"
      ? getPresentationFilename(item.conversationTitle || item.conversation_title, item.filename || item.name)
      : item.filename || item.name || null,
    conversationId: item.conversationId || item.conversation_id || null,
    conversationTitle: item.conversationTitle || item.conversation_title || null,
    isGroup: item.isGroup === true || item.is_group === true,
    createdAt: item.createdAt || item.created_at || null,
    mimeType: item.mimeType || item.mime_type || null,
  };
};

/** Fetches the signed-in user's uploaded and generated assets. */
export const getPersonalLibraryPage = async (cursor: string | null = null, limit = 24, search = ""): Promise<PersonalLibraryPage> => {
  const params = new URLSearchParams({ cursor: cursor || "", limit: String(limit), search });
  const response = await fetchWithAuth(`/api/library/personal?${params.toString()}`, { method: "GET" });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as Partial<ErrorResponsePayload> | null;
    throw apiRequestErrorFromResponse(response, payload, "Unable to load your library.");
  }
  const payload = await response.json();
  return {
    items: Array.isArray(payload?.items) ? payload.items.map(mapPersonalLibraryItem) : [],
    nextCursor: payload?.nextCursor || null,
    hasMore: payload?.hasMore === true,
  };
};



/****************************************************************************************************************************************************************************************************************************
 * USERS LIBRARY
/***************************************************************************************************************************************************************************************************************************/

/**
 * Get groups and users
 * 
 * @param {number} source - The source type (0 for groups, 1 for users)
 * @param {string} search - The search string to filter users
 * @returns {Promise<Response>} A promise that resolves to the response object
**/
export const getUsers = async (source: number, search: string): Promise<Response> => {
  const response = await fetchWithAuth(`/api/users?source=${source}&search=${encodeURIComponent(search)}`)
  .then((res) => {
    return res;
  })
  .catch((err) => {
    console.error("There was an issue fetching users.");
    return new Response;
  });

  return response;
}



/****************************************************************************************************************************************************************************************************************************
 * ANALYTICS
/***************************************************************************************************************************************************************************************************************************/

/**
 * Get groups and users
 * 
 * @param {string} type - Event type
 * @param {string} item - Event item
 * @param {string} text - Event text
 * @param {string} conversation_id - Conversation ID
 * @param {string} cgpt_id - Custom GPT ID
 * @param {string} agent_id - Agent ID
**/
export const trackEvent = (type: string, item: string, text: string, conversation_id: string, cgpt_id: string, agent_id: string) => {
  fetchWithAuth(`/api/analytics/track`, {
    method: "POST",
    body: JSON.stringify({
      type: type,
      item: item,
      text: text,
      conversation_id: conversation_id,
      cgpt_id: cgpt_id,
      agent_id: agent_id
    }),
    headers: { "Content-Type": "application/json" }
  });
}



/****************************************************************************************************************************************************************************************************************************
 * GROUP CONVERSATIONS
/***************************************************************************************************************************************************************************************************************************/

const mapGroupConversation = (conv: any): Conversation => ({
  id: conv.id,
  title: conv.title,
  date: conv.updatedAt,
  model: chatModels.find(m => m.deployment === conv.deployment) || (chatModels.find(m => m.default) || chatModels[0]),
  reasoning: conv.reasoning || '',
  customGPTID: conv.customGPTID || '',
  selected_tool: conv.selectedTool || '',
  files: conv.files || undefined,
  filename: conv.filename || undefined,
  direct_files: conv.directFiles || false,
  messages: [],
  migrated: false,
  shared: 0,
  isGroup: true,
  participants: conv.participants || [],
  ownerId: conv.ownerId || '',
  ownerName: conv.ownerName || '',
});

const mapGroupMessage = (msg: any): ChatMessage => ({
  id: msg.id,
  role: msg.role,
  date: msg.createdAt,
  anchorMessageId: msg.anchorMessageId || undefined,
  userId: msg.senderId || '',
  content: msg.content || '',
  context: msg.context || '',
  model: msg.model || '',
  senderName: msg.senderName || '',
  files: msg.files || '',
  filename: msg.filename || '',
  direct_files: msg.directFiles || false,
});

export const getGroupConversations = async (): Promise<Conversation[]> => {
  return fetchWithAuth("/api/conversation/group/list")
    .then(async (res) => {
      const payload = await res.json();
      return (payload as any[]).map(mapGroupConversation);
    })
    .catch(() => []);
};

export const convertToGroupConversation = async (conversationId: string): Promise<Conversation | null> => {
  return fetchWithAuth("/api/conversation/group/convert", {
    method: "POST",
    body: JSON.stringify({ conversation_id: conversationId }),
    headers: { "Content-Type": "application/json" }
  })
    .then(async (res) => {
      if (!res.ok) return null;
      const payload = await res.json();
      return mapGroupConversation(payload);
    })
    .catch(() => null);
};

export const joinGroupConversation = async (conversationId: string): Promise<boolean> => {
  const response = await fetchWithAuth("/api/conversation/group/join", {
    method: "POST",
    body: JSON.stringify({ conversation_id: conversationId }),
    headers: { "Content-Type": "application/json" }
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as Partial<ErrorResponsePayload> | null;
    throw apiRequestErrorFromResponse(response, payload, "Failed to join the conversation.");
  }
  return true;
};

export const loadGroupConversation = async (
  conversationId: string,
): Promise<{ conversation: Conversation; messages: ChatMessage[]; nextCursor: string } | null> => {
  return fetchWithAuth("/api/conversation/group/load", {
    method: "POST",
    body: JSON.stringify({ conversation_id: conversationId }),
    headers: { "Content-Type": "application/json" }
  })
    .then(async (res) => {
      if (!res.ok) return null;
      const payload = await res.json();
      const conversation = mapGroupConversation(payload.conversation);
      if (Array.isArray(payload.presentations)) {
        conversation.presentations = payload.presentations.map((presentation: PresentationAsset) => ({
          ...presentation,
          filename: getPresentationFilename(conversation.title, presentation.filename),
        }));
      }
      const ownerName = conversation.ownerName || '';
      const messages = normalizeCompactionTimeline((payload.messages as any[]).map(msg => {
        const mapped = mapGroupMessage(msg);
        if (!mapped.senderName && mapped.role === 'user') mapped.senderName = ownerName;
        return mapped;
      }), { dropOrphans: true });
      return { conversation, messages, nextCursor: payload.nextCursor || payload.conversation?.updatedAt || '' };
    })
    .catch(() => null);
};

export const pollGroupConversation = async (
  conversationId: string,
  since: string
): Promise<{ messages: ChatMessage[]; presentations: PresentationAsset[]; participants: GroupParticipant[]; updatedAt: string; nextCursor: string } | "access_denied" | null> => {
  return fetchWithAuth("/api/conversation/group/poll", {
    method: "POST",
    body: JSON.stringify({ conversation_id: conversationId, since }),
    headers: { "Content-Type": "application/json" }
  })
    .then(async (res) => {
      if (res.status === 403) return "access_denied" as const;
      if (!res.ok) return null;
      const payload = await res.json();
      return {
        messages: (payload.messages as any[]).map(mapGroupMessage),
        presentations: Array.isArray(payload.presentations) ? payload.presentations as PresentationAsset[] : [],
        participants: payload.participants || [],
        updatedAt: payload.updatedAt || '',
        nextCursor: payload.nextCursor || since || '',
      };
    })
    .catch(() => null);
};

export const generateGroupConversation = async (
  request: ConversationRequest,
  currentModel: ChatModel,
  currentCustomGPT: CustomGPT | null,
  currentAgent: Agent | null,
  abortSignal: AbortSignal,
  conversationId: string,
  timezone: string,
  selectedTool?: string,
  filesStr?: string,
  filename?: string,
  addedFile?: boolean,
  initialFile?: boolean,
  directFiles?: boolean,
  imageBlobURL?: string,
  imageSize?: string,
  imageStyle?: string,
  marketingSearchLevel?: string,
  marketingGuid?: string,
  continuation?: boolean,
  personalization?: Personalization
): Promise<Response> => {
  const body = {
    messages: request.messages,
    conversation_id: conversationId,
    currentModel: currentModel,
    currentCustomGPT: currentCustomGPT,
    currentAgent: currentAgent,
    timezone,
    selected_tool: selectedTool || '',
    force_generate: (selectedTool || '') === 'image_generation',
    files_str: filesStr || '',
    filename: filename || '',
    added_file: addedFile || false,
    initial_file: initialFile || false,
    direct_files: directFiles || false,
    image_blob_url: imageBlobURL || '',
    image_size: imageSize || '1024x1024',
    image_style: imageStyle || '',
    marketing_search_level: marketingSearchLevel || 'medium',
    marketing_guid: marketingGuid || '',
    continuation: continuation || false,
    personalization: personalization || DEFAULT_PERSONALIZATION,
  };

  return fetchWithAuth("/api/conversation/group/generate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal: abortSignal,
  });
};

export const saveGroupResponse = async (conversationId: string, message: ChatMessage): Promise<Response> => {
  return fetchWithAuth("/api/conversation/group/save_response", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ conversation_id: conversationId, message }),
  });
};

export const inviteGroupParticipant = async (conversationId: string, participant: GroupParticipant): Promise<boolean> => {
  return fetchWithAuth("/api/conversation/group/invite", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ conversation_id: conversationId, participant }),
  })
    .then(async (res) => {
      const payload = await res.json();
      return payload.success === true;
    })
    .catch(() => false);
};

export const removeGroupParticipant = async (conversationId: string, participantUserId: string): Promise<boolean> => {
  return fetchWithAuth("/api/conversation/group/remove_participant", {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ conversation_id: conversationId, participant_user_id: participantUserId }),
  })
    .then((res) => res.ok)
    .catch(() => false);
};

export const leaveGroupConversation = async (conversationId: string): Promise<boolean> => {
  return fetchWithAuth("/api/conversation/group/leave", {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ conversation_id: conversationId }),
  })
    .then((res) => res.ok)
    .catch(() => false);
};

export const renameGroupConversation = async (conversationId: string, title: string): Promise<boolean> => {
  return fetchWithAuth("/api/conversation/group/rename", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ conversation_id: conversationId, title }),
  })
    .then((res) => res.ok)
    .catch(() => false);
};

export const deleteGroupConversation = async (conversationId: string): Promise<boolean> => {
  return fetchWithAuth("/api/conversation/group/delete", {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ conversation_id: conversationId }),
  })
    .then((res) => res.ok)
    .catch(() => false);
};
